import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { User, UserCheck, X } from 'lucide-react'
import { getStorageItem, setStorageItem, removeStorageItem } from '@/utils/storage'

export function ProfileCompletionDialog() {
  const { user } = useAuth()
  const [showDialog, setShowDialog] = useState(false)
  const [profileData, setProfileData] = useState<any>(null)
  const navigate = useNavigate()

  

  useEffect(() => {
    if (user) {
      checkProfileCompleteness()
    }
  }, [user])

  // Check if this is a newly verified user
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const isFromEmailVerification = urlParams.has('token_hash') || urlParams.has('type')

    if (isFromEmailVerification && user) {
      // Clear the localStorage flag for email-verified users to ensure dialog shows
      removeStorageItem('hasSeenProfilePrompt')
      // Small delay to ensure profile data is loaded
      setTimeout(() => {
        checkProfileCompleteness()
      }, 1000)
    }
  }, [user])

  const checkProfileCompleteness = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error) throw error

      setProfileData(data)

      // Check if profile is incomplete
      const isIncomplete = !data || !data.display_name || !data.bio || !data.location

      // Show dialog if profile is incomplete
      // We'll also check if this is likely a new user by checking localStorage
      const hasSeenProfilePrompt = getStorageItem<boolean>('hasSeenProfilePrompt', { fallback: false })

      // Show dialog if profile is incomplete and user hasn't seen the prompt before
      if (isIncomplete && !hasSeenProfilePrompt) {
        setShowDialog(true)
      }
    } catch (error) {
      console.error('Error checking profile completeness:', error)
    }
  }

  const handleCompleteProfile = () => {
    setStorageItem('hasSeenProfilePrompt', true)
    setShowDialog(false)
    navigate('/profile')
  }

  const handleSkip = () => {
    setStorageItem('hasSeenProfilePrompt', true)
    setShowDialog(false)
  }

  if (!showDialog) return null

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            Complete Your Profile
          </DialogTitle>
          <DialogDescription>
            Welcome to Indie Scene Jobs! To get the most out of our platform and help filmmakers
            find you, please complete your profile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <User className="h-4 w-4" />
              What's missing:
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              {!profileData?.display_name && <li>• Display name</li>}
              {!profileData?.bio && <li>• Bio/About section</li>}
              {!profileData?.location && <li>• Location</li>}
            </ul>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleCompleteProfile} className="flex-1">
              Complete Profile
            </Button>
            <Button variant="outline" onClick={handleSkip} className="flex-1">
              <X className="h-4 w-4 mr-2" />
              Skip for now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
